import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import "./stock-details.css";
import { fetchProducts } from "../Redux/Actions/productVActions";

const StockDetails = () => {
  const dispatch = useDispatch();

  const [stockData, setStockData] = useState([]);
  const [filteredStock, setFilteredStock] = useState([]);
  const [sortedStock, setSortedStock] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState([]);
  const [initialStockLevels, setInitialStockLevels] = useState({});

  const productsData = useSelector(
    (state) => state.productVReducer?.products || []
  );

  // ---------- Fonctions utilitaires ----------
  const idOf = (v) => {
    if (!v) return null;
    if (typeof v === "string") return v;
    if (typeof v === "object" && v._id) return String(v._id);
    return null;
  };

  const findProductInRedux = useCallback(
    (productId) =>
      productsData?.find((p) => String(p._id) === String(productId)) || null,
    [productsData]
  );

  const findSubProductNameInRedux = useCallback(
    (subProductId) => {
      for (const p of productsData) {
        if (!p.subProductsV) continue;
        for (const sp of p.subProductsV) {
          if (String(idOf(sp)) === String(subProductId)) {
            return sp.name;
          }
        }
      }
      return null;
    },
    [productsData]
  );

  const findSubProductParent = useCallback(
    (subProductId) => {
      for (const product of productsData) {
        if (!product.subProductsV) continue;
        for (const sp of product.subProductsV) {
          if (String(idOf(sp)) === String(subProductId)) {
            return product;
          }
        }
      }
      return null;
    },
    [productsData]
  );

  const getProductName = (productId, item) => {
    if (!productId) return "—";
    return item.productV?.name || findProductInRedux(productId)?.name || "—";
  };

  const getSubProductName = (subProductId, item) => {
    if (!subProductId) return "—";
    return (
      item.subProductV?.name || findSubProductNameInRedux(subProductId) || "—"
    );
  };

  const getCategory = (item) => {
    if (item.productV) {
      const p = item.productV?.category || findProductInRedux(idOf(item.productV))?.category;
      return p || "Inconnue";
    }

    if (item.subProductV) {
      const parent = findSubProductParent(idOf(item.subProductV));
      return parent?.category || "Inconnue";
    }

    return "Inconnue";
  };

  const getLocationName = (item) => {
    return (
      item.subProductV?.localisation?.name ||
      item.productV?.localisation?.name ||
      "Inconnue"
    );
  };

  // ---------- Fonction pour obtenir le pourcentage de stock ----------
  const getStockPercentage = useCallback((item) => {
    const id = item.productV?._id || item.subProductV?._id;
    if (!id) return 100;
    
    const initialQty = initialStockLevels[id];
    if (!initialQty || initialQty === 0) return 100;
    
    return (item.quantity / initialQty) * 100;
  }, [initialStockLevels]);

  // ---------- Fonction pour déterminer la classe CSS selon l'état du stock ----------
  const getStockStatusClass = useCallback((item) => {
    if (item.quantity <= 0) return "out-of-stock";
    
    const percentage = getStockPercentage(item);
    if (percentage <= 50) return "low-stock";
    if (percentage >= 90) return "good-stock";
    
    return "normal-stock";
  }, [getStockPercentage]);

  // ---------- Charger les localisations ----------
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get("/api/Locations/getAllLocations");
        setLocations(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Erreur chargement localisations :", err);
        setLocations([]);
      }
    };
    fetchLocations();
  }, []);

  // ---------- Charger les produits ----------
  useEffect(() => {
    if (!productsData.length) {
      dispatch(fetchProducts());
    }
  }, [dispatch, productsData]);

  // ---------- Charger le stock initial ----------
  useEffect(() => {
    const fetchInitialStock = async () => {
      try {
        const res = await axios.get("/api/StockV/initial-stock-levels");
        setInitialStockLevels(res.data || {});
      } catch (error) {
        console.error("Erreur chargement stock initial :", error);
        // Fallback: utiliser les données actuelles comme référence
        const fallbackInitial = {};
        stockData.forEach(item => {
          const id = item.productV?._id || item.subProductV?._id;
          if (id) fallbackInitial[id] = item.quantity * 2; // Simuler un stock initial
        });
        setInitialStockLevels(fallbackInitial);
      }
    };
    if (stockData.length > 0) {
      fetchInitialStock();
    }
  }, [stockData]);

  // ---------- Charger le stock ----------
  useEffect(() => {
    const fetchStock = async () => {
      try {
        const res = await axios.get("/api/StockV/stock-report");
        setStockData(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Erreur chargement stock :", error);
        setStockData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStock();
  }, []);

  // ---------- Charger les données de vente ----------
  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        const res = await axios.get("/api/Sales/top-products");
        // S'assurer d'avoir toujours 5 éléments même si moins de ventes
        const sales = Array.isArray(res.data) ? res.data : [];
        const filledSales = [...sales];
        while (filledSales.length < 5) {
          filledSales.push({
            _id: `placeholder-${filledSales.length}`,
            productName: "Aucune vente significative",
            totalSold: 0,
            isPlaceholder: true
          });
        }
        setSalesData(filledSales.slice(0, 5));
      } catch (error) {
        console.error("Erreur chargement ventes :", error);
        // Données par défaut
        setSalesData([
          { _id: "1", productName: "Aucune donnée disponible", totalSold: 0, isPlaceholder: true },
          { _id: "2", productName: "Chargement...", totalSold: 0, isPlaceholder: true },
          { _id: "3", productName: "Chargement...", totalSold: 0, isPlaceholder: true },
          { _id: "4", productName: "Chargement...", totalSold: 0, isPlaceholder: true },
          { _id: "5", productName: "Chargement...", totalSold: 0, isPlaceholder: true }
        ]);
      }
    };
    fetchSalesData();
  }, []);

  // ---------- Filtrer selon localisation ----------
  useEffect(() => {
    if (!selectedLocation) {
      setFilteredStock(stockData);
    } else {
      const filtered = stockData.filter((item) => {
        const productLoc = item.productV?.localisation?._id;
        const subProductLoc = item.subProductV?.localisation?._id;
        return (
          String(productLoc) === selectedLocation ||
          String(subProductLoc) === selectedLocation
        );
      });
      setFilteredStock(filtered);
    }
  }, [selectedLocation, stockData]);

  // ---------- Trier le stock (rupture en premier) ----------
  useEffect(() => {
    const sorted = [...filteredStock].sort((a, b) => {
      // Rupture de stock en premier
      if (a.quantity <= 0 && b.quantity > 0) return -1;
      if (a.quantity > 0 && b.quantity <= 0) return 1;
      
      // Ensuite par pourcentage de baisse
      const aPercent = getStockPercentage(a);
      const bPercent = getStockPercentage(b);
      
      if (aPercent < bPercent) return -1;
      if (aPercent > bPercent) return 1;
      
      return 0;
    });
    setSortedStock(sorted);
  }, [filteredStock, initialStockLevels, getStockPercentage]);

  // ---------- Calcul des métriques ----------
  const calculateMetrics = useCallback(() => {
    const dataToUse = selectedLocation ? filteredStock : stockData;
    
    // Total quantité par localisation
    const quantityByLocation = locations.map(location => {
      const locationStock = stockData.filter((item) => {
        const productLoc = item.productV?.localisation?._id;
        const subProductLoc = item.subProductV?.localisation?._id;
        return String(productLoc) === String(location._id) || 
               String(subProductLoc) === String(location._id);
      });

      const totalQuantity = locationStock.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const productCount = new Set(
        locationStock
          .filter(item => item.productV)
          .map(item => String(item.productV?._id))
      ).size;

      const subProductCount = new Set(
        locationStock
          .filter(item => item.subProductV)
          .map(item => String(item.subProductV?._id))
      ).size;

      const outOfStockCount = locationStock.filter(item => item.quantity <= 0).length;

      return {
        location: location.name,
        totalQuantity,
        totalProducts: productCount + subProductCount,
        outOfStockCount
      };
    });

    // Rupture de stock
    const outOfStock = dataToUse.filter(item => item.quantity <= 0).length;

    // Produits avec baisse significative (>50%)
    const significantDrop = dataToUse.filter(item => {
      const percentage = getStockPercentage(item);
      return percentage <= 50 && percentage > 0;
    }).length;

    // Calcul du pourcentage de stock global
    const calculateGlobalStockPercentage = () => {
      const totalInitialValue = Object.values(initialStockLevels).reduce((sum, val) => sum + val, 0);
      const currentStockValue = dataToUse.reduce((sum, item) => sum + (item.quantity || 0), 0);
      
      if (totalInitialValue === 0) return 100;
      return ((currentStockValue / totalInitialValue) * 100).toFixed(1);
    };

    // Top 5 des meilleures ventes
    const topSales = salesData.slice(0, 5);

    return {
      quantityByLocation,
      outOfStock,
      significantDrop,
      stockPercentage: calculateGlobalStockPercentage(),
      topSales,
      totalProducts: dataToUse.length,
      totalQuantity: dataToUse.reduce((sum, item) => sum + (item.quantity || 0), 0)
    };
  }, [stockData, filteredStock, selectedLocation, locations, salesData, initialStockLevels, getStockPercentage]);

  const metrics = calculateMetrics();

  // ---------- Affichage ----------
  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Chargement du tableau de bord...</p>
    </div>
  );

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>📊 Tableau de Bord - État du Stock</h2>
        <div className="last-update">
          Dernière mise à jour : {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* ✅ Filtre Localisation */}
      <div className="filter-section">
        <label>Filtrer par localisation :</label>
        <select
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
        >
          <option value="">🌍 Toutes les localisations</option>
          {locations.map((loc) => (
            <option key={loc._id} value={loc._id}>
              {loc.name}
            </option>
          ))}
        </select>
      </div>

      {/* ✅ Cartes de métriques */}
      <div className="metrics-grid">
        <div className="metric-card total-stock">
          <div className="metric-icon">📦</div>
          <div className="metric-content">
            <h3>Stock Total</h3>
            <div className="metric-value">{metrics.totalQuantity.toFixed(2)}</div>
            <div className="metric-label">Unités en stock</div>
          </div>
        </div>

        <div className="metric-card total-products">
          <div className="metric-icon">🏷️</div>
          <div className="metric-content">
            <h3>Produits Identifiés</h3>
            <div className="metric-value">{metrics.totalProducts}</div>
            <div className="metric-label">Produits différents</div>
          </div>
        </div>

        <div className="metric-card out-of-stock-card">
          <div className="metric-icon">⚠️</div>
          <div className="metric-content">
            <h3>Ruptures de Stock</h3>
            <div className="metric-value">{metrics.outOfStock}</div>
            <div className="metric-label">Produits épuisés</div>
          </div>
        </div>

        <div className="metric-card low-stock-card">
          <div className="metric-icon">📉</div>
          <div className="metric-content">
            <h3>Stock Faible</h3>
            <div className="metric-value">{metrics.significantDrop}</div>
            <div className="metric-label">Baisse > 50%</div>
          </div>
        </div>

        <div className="metric-card stock-level">
          <div className="metric-icon">📊</div>
          <div className="metric-content">
            <h3>Niveau de Stock</h3>
            <div className="metric-value">{metrics.stockPercentage}%</div>
            <div className="metric-label">Par rapport au stock initial</div>
          </div>
        </div>
      </div>

      {/* ✅ Répartition par localisation */}
      <div className="section">
        <div className="section-header">
          <h3>📦 Répartition par Localisation</h3>
          <span className="section-badge">{locations.length} localisations</span>
        </div>
        <div className="locations-grid">
          {metrics.quantityByLocation.map((loc, index) => (
            <div key={index} className="location-card">
              <h4>{loc.location}</h4>
              <div className="location-metrics">
                <div className="location-metric">
                  <span className="value">{loc.totalQuantity.toFixed(2)}</span>
                  <span className="label">Quantité</span>
                </div>
                <div className="location-metric">
                  <span className="value">{loc.totalProducts}</span>
                  <span className="label">Produits</span>
                </div>
                {loc.outOfStockCount > 0 && (
                  <div className="location-metric warning">
                    <span className="value">{loc.outOfStockCount}</span>
                    <span className="label">Ruptures</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ Top 5 des meilleures ventes */}
      <div className="section">
        <div className="section-header">
          <h3>🏆 Top 5 des Meilleures Ventes</h3>
          <span className="section-badge">30 derniers jours</span>
        </div>
        <div className="sales-list">
          {metrics.topSales.map((sale, index) => (
            <div key={sale._id} className={`sale-item ${sale.isPlaceholder ? 'placeholder' : ''}`}>
              <span className="rank">#{index + 1}</span>
              <span className="product-name">{sale.productName}</span>
              <span className="sales-count">
                {sale.totalSold > 0 ? `${sale.totalSold} vendus` : 'Aucune vente'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ Tableau détaillé du stock */}
      <div className="section">
        <div className="section-header">
          <h3>📋 Détail du Stock</h3>
          <span className="section-badge">{sortedStock.length} produits</span>
        </div>
        
        {/* Légende */}
        <div className="legend">
          <div className="legend-item">
            <div className="legend-color out-of-stock"></div>
            <span>Rupture de stock</span>
          </div>
          <div className="legend-item">
            <div className="legend-color low-stock"></div>
            <span>Stock faible (&lt;50%)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color good-stock"></div>
            <span>Stock normal</span>
          </div>
        </div>

        <div className="table-container">
          <table className="stock-table">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Catégorie</th>
                <th>Produit</th>
                <th>Sous-produit</th>
                <th>Quantité</th>
                <th>Stock %</th>
                <th>Unité</th>
                <th>Localisation</th>
                <th>Dernière Mise à Jour</th>
              </tr>
            </thead>
            <tbody>
              {sortedStock.length > 0 ? (
                sortedStock.map((item, index) => {
                  const stockPercentage = getStockPercentage(item);
                  return (
                    <tr key={index} className={getStockStatusClass(item)}>
                      <td>
                        <div className="status-indicator">
                          {item.quantity <= 0 ? (
                            <span className="status-badge out-of-stock">Rupture</span>
                          ) : stockPercentage <= 50 ? (
                            <span className="status-badge low-stock">Faible</span>
                          ) : (
                            <span className="status-badge good-stock">Normal</span>
                          )}
                        </div>
                      </td>
                      <td>{getCategory(item)}</td>
                      <td>{getProductName(idOf(item.productV), item)}</td>
                      <td>{getSubProductName(idOf(item.subProductV), item)}</td>
                      <td className="quantity-cell">
                        {item.quantity?.toFixed(2)}
                      </td>
                      <td className="percentage-cell">
                        {stockPercentage.toFixed(1)}%
                      </td>
                      <td>{item.unit}</td>
                      <td>{getLocationName(item)}</td>
                      <td>
                        {item.lastUpdated
                          ? new Date(item.lastUpdated).toLocaleDateString('fr-FR')
                          : "N/A"}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="no-data">
                    <div className="no-data-content">
                      <span>📭</span>
                      <p>Aucune donnée de stock trouvée pour cette localisation</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockDetails;